import Swiper from 'swiper';

const MOBILE_BP = 480;

export const TestimonialsCarousel = {
	_instances: [],

	init() {
		document.querySelectorAll( '[data-testimonials-carousel]' ).forEach( ( carousel ) => {
			if ( carousel.dataset.testimonialsInit === '1' ) return; // Idempotent for editor re-mounts.
			carousel.dataset.testimonialsInit = '1';
			const instance = { carousel, mode: null, swiper: null, cleanup: null };
			this._instances.push( instance );
			this._setup( instance );
		} );

		let resizeTimer;
		window.addEventListener( 'resize', () => {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( () => {
				this._instances.forEach( ( inst ) => {
					const shouldBeMobile = window.innerWidth <= MOBILE_BP;
					const isMobile = inst.mode === 'mobile';
					if ( shouldBeMobile !== isMobile ) {
						this._destroy( inst );
						this._setup( inst );
					}
				} );
			}, 300 );
		} );
	},

	_destroy( instance ) {
		if ( instance.swiper ) {
			instance.swiper.destroy( true, true );
			instance.swiper = null;

			const carousel = instance.carousel;
			const wrapper = carousel.querySelector( '.swiper-wrapper' );
			if ( wrapper ) {
				const cards = Array.from( wrapper.querySelectorAll( '[data-card]' ) );
				cards.forEach( ( card ) => carousel.appendChild( card ) );
				wrapper.remove();
			}
			carousel.classList.remove( 'swiper', 'swiper-initialized', 'swiper-horizontal' );
		}

		if ( instance.cleanup ) {
			instance.cleanup();
			instance.cleanup = null;
		}

		instance.mode = null;
	},

	_setup( instance ) {
		const { carousel } = instance;
		const section = carousel.closest( '.block-testimonials' );
		const cards = Array.from( carousel.querySelectorAll( '[data-card]' ) );
		const counterEl = section?.querySelector( '[data-testimonials-current]' );
		const prevBtn = section?.querySelector( '[data-testimonials-prev]' );
		const nextBtn = section?.querySelector( '[data-testimonials-next]' );
		const total = cards.length;

		if ( total < 2 ) return;

		if ( window.innerWidth <= MOBILE_BP ) {
			instance.mode = 'mobile';
			this._initMobile( instance, carousel, cards, counterEl, prevBtn, nextBtn, total );
		} else {
			instance.mode = 'desktop';
			this._initDesktop( instance, carousel, cards, counterEl, prevBtn, nextBtn, total, section );
		}
	},

	_initMobile( instance, carousel, cards, counterEl, prevBtn, nextBtn, total ) {
		carousel.classList.add( 'swiper' );

		const wrapper = document.createElement( 'div' );
		wrapper.className = 'swiper-wrapper';

		cards.forEach( ( card ) => {
			card.removeAttribute( 'data-slot' );
			card.style.cssText = '';
			const slide = document.createElement( 'div' );
			slide.className = 'swiper-slide';
			slide.appendChild( card );
			wrapper.appendChild( slide );
		} );

		carousel.appendChild( wrapper );

		const swiper = new Swiper( carousel, {
			slidesPerView: 1,
			spaceBetween: 16,
			loop: total > 2,
			autoplay: { delay: 5000, disableOnInteraction: true },
			threshold: 10,
			touchAngle: 35,
			touchReleaseOnEdges: true,
			on: {
				slideChange() {
					if ( counterEl ) {
						counterEl.textContent = String( ( this.realIndex || 0 ) + 1 ).padStart( 2, '0' );
					}
				},
			},
		} );

		instance.swiper = swiper;

		const onNext = () => swiper.slideNext();
		const onPrev = () => swiper.slidePrev();
		if ( nextBtn ) nextBtn.addEventListener( 'click', onNext );
		if ( prevBtn ) prevBtn.addEventListener( 'click', onPrev );

		instance.cleanup = () => {
			if ( nextBtn ) nextBtn.removeEventListener( 'click', onNext );
			if ( prevBtn ) prevBtn.removeEventListener( 'click', onPrev );
		};
	},

	_initDesktop( instance, carousel, cards, counterEl, prevBtn, nextBtn, total, section ) {
		const uniqueTotal = total;

		// The arc shows 5 positions (slots -2 … +2).  With fewer than 5
		// cards the wrap-around forces visible cards to jump across the
		// screen.  Duplicating the DOM nodes so total ≥ 5 pushes the wrap
		// into the invisible ±2 slots where it can't be seen.
		if ( total < 5 ) {
			const origCards = [ ...cards ];
			while ( cards.length < 5 ) {
				origCards.forEach( ( c ) => {
					const clone = c.cloneNode( true );
					clone.removeAttribute( 'data-card' );
					clone.setAttribute( 'data-testimonial-clone', '' );
					carousel.appendChild( clone );
					cards.push( clone );
				} );
			}
			total = cards.length;
		}

		let current = 0;
		let animating = false;

		function getSlot( i ) {
			let diff = i - current;
			if ( diff > Math.floor( total / 2 ) ) diff -= total;
			if ( diff < -Math.floor( total / 2 ) ) diff += total;
			return diff;
		}

		function setCarouselHeight() {
			let maxH = 0;
			cards.forEach( ( c ) => {
				c.style.position = 'absolute';
				c.style.visibility = 'hidden';
				c.style.opacity = '1';
				const h = c.scrollHeight;
				c.style.position = '';
				c.style.visibility = '';
				c.style.opacity = '';
				if ( h > maxH ) maxH = h;
			} );
			if ( maxH > 0 ) carousel.style.height = ( maxH ) + 'px';
		}

		function applySlots() {
			cards.forEach( ( c, i ) => {
				const slot = getSlot( i );
				if ( slot >= -2 && slot <= 2 ) {
					c.setAttribute( 'data-slot', String( slot ) );
				} else {
					c.setAttribute( 'data-slot', 'hidden' );
				}
			} );
		}

		function updateCounter() {
			if ( counterEl ) {
				counterEl.textContent = String( ( current % uniqueTotal ) + 1 ).padStart( 2, '0' );
			}
		}

		function goTo( direction ) {
			if ( animating ) return;
			animating = true;
			current = ( current + direction + total ) % total;
			applySlots();
			updateCounter();
			setTimeout( () => { animating = false; }, 850 );
		}

		function goToIndex( i ) {
			if ( animating || i === current ) return;
			animating = true;
			current = i;
			applySlots();
			updateCounter();
			setTimeout( () => { animating = false; }, 850 );
		}

		// Autoplay manager — single source of truth so manual interactions
		// can reset the timer (otherwise a click + tick fire back-to-back),
		// and so a tick that lands during an animation isn't dropped.
		const AUTOPLAY_MS = 6000;
		let autoplayTimer = null;
		let autoplayPaused = false;
		const stopAutoplay = () => {
			if ( autoplayTimer ) {
				clearInterval( autoplayTimer );
				autoplayTimer = null;
			}
		};
		const startAutoplay = () => {
			stopAutoplay();
			if ( autoplayPaused ) return;
			autoplayTimer = setInterval( () => {
				if ( animating || isDragging ) return; // skip this beat, next tick retries
				goTo( 1 );
			}, AUTOPLAY_MS );
		};

		const onNext = () => { goTo( 1 ); startAutoplay(); };
		const onPrev = () => { goTo( -1 ); startAutoplay(); };
		if ( nextBtn ) nextBtn.addEventListener( 'click', onNext );
		if ( prevBtn ) prevBtn.addEventListener( 'click', onPrev );

		const cardClickHandlers = [];
		cards.forEach( ( card, i ) => {
			const handler = ( e ) => {
				const slot = getSlot( i );
				if ( slot === 0 ) return;
				e.preventDefault();
				goToIndex( i );
				startAutoplay();
			};
			card.addEventListener( 'click', handler );
			cardClickHandlers.push( { card, handler } );
		} );

		let touchStartX = 0;
		let touchStartY = 0;
		const onTouchStart = ( e ) => {
			touchStartX = e.touches[ 0 ].clientX;
			touchStartY = e.touches[ 0 ].clientY;
		};
		const onTouchEnd = ( e ) => {
			const dx = e.changedTouches[ 0 ].clientX - touchStartX;
			const dy = e.changedTouches[ 0 ].clientY - touchStartY;
			if ( Math.abs( dx ) > 50 && Math.abs( dx ) > Math.abs( dy ) ) {
				goTo( dx < 0 ? 1 : -1 );
				startAutoplay();
			}
		};
		carousel.addEventListener( 'touchstart', onTouchStart, { passive: true } );
		carousel.addEventListener( 'touchend', onTouchEnd, { passive: true } );

		let isDragging = false;
		let dragStartX = 0;
		carousel.style.cursor = 'grab';

		const onMouseDown = ( e ) => {
			isDragging = true;
			dragStartX = e.clientX;
			carousel.style.cursor = 'grabbing';
		};
		const onMouseUp = ( e ) => {
			if ( ! isDragging ) return;
			isDragging = false;
			carousel.style.cursor = 'grab';
			const dx = e.clientX - dragStartX;
			if ( Math.abs( dx ) > 50 ) {
				goTo( dx < 0 ? 1 : -1 );
				startAutoplay();
			}
		};
		carousel.addEventListener( 'mousedown', onMouseDown );
		document.addEventListener( 'mouseup', onMouseUp );

		const onEnter = () => { autoplayPaused = true; stopAutoplay(); };
		const onLeave = () => { autoplayPaused = false; if ( ! isDragging ) startAutoplay(); };
		if ( section ) {
			section.addEventListener( 'mouseenter', onEnter );
			section.addEventListener( 'mouseleave', onLeave );
		}

		// Pause autoplay when the tab is hidden — otherwise the carousel
		// burns through cycles in the background and feels desynced when
		// you come back.
		const onVisibility = () => {
			if ( document.hidden ) stopAutoplay();
			else if ( ! autoplayPaused ) startAutoplay();
		};
		document.addEventListener( 'visibilitychange', onVisibility );

		startAutoplay();

		const onResize = () => setCarouselHeight();
		window.addEventListener( 'resize', onResize );

		setCarouselHeight();
		applySlots();
		updateCounter();

		instance.cleanup = () => {
			if ( nextBtn ) nextBtn.removeEventListener( 'click', onNext );
			if ( prevBtn ) prevBtn.removeEventListener( 'click', onPrev );
			cardClickHandlers.forEach( ( { card, handler } ) => card.removeEventListener( 'click', handler ) );
			carousel.removeEventListener( 'touchstart', onTouchStart );
			carousel.removeEventListener( 'touchend', onTouchEnd );
			carousel.removeEventListener( 'mousedown', onMouseDown );
			document.removeEventListener( 'mouseup', onMouseUp );
			if ( section ) {
				section.removeEventListener( 'mouseenter', onEnter );
				section.removeEventListener( 'mouseleave', onLeave );
			}
			window.removeEventListener( 'resize', onResize );
			document.removeEventListener( 'visibilitychange', onVisibility );
			stopAutoplay();
			carousel.style.height = '';
			carousel.style.cursor = '';
			cards.forEach( ( c ) => {
				c.removeAttribute( 'data-slot' );
				c.style.cssText = '';
			} );
			carousel.querySelectorAll( '[data-testimonial-clone]' ).forEach( ( el ) => el.remove() );
		};
	},
};
